import styled, { keyframes } from "styled-components";

// ─── Motion ─────────────────────────────────────────────────────────────────

const softPulse = keyframes`
  0%, 100% {
    box-shadow:
      0 18px 36px rgba(var(--color-brand-600-rgb), 0.28),
      0 4px 10px rgba(var(--color-brand-600-rgb), 0.14),
      inset 0 2px 0 rgba(255, 255, 255, 0.6),
      inset 0 -4px 8px rgba(var(--color-brand-700-rgb), 0.28);
  }
  50% {
    box-shadow:
      0 22px 44px rgba(var(--color-brand-600-rgb), 0.38),
      0 6px 14px rgba(var(--color-brand-600-rgb), 0.22),
      inset 0 2px 0 rgba(255, 255, 255, 0.7),
      inset 0 -4px 10px rgba(var(--color-brand-700-rgb), 0.34);
  }
`;

// ─── Outer frosted glass shell ──────────────────────────────────────────────

export const CreateInputShell = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px 6px 22px;
  min-height: 60px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.75);
  background:
    radial-gradient(
      120% 100% at 20% 0%,
      rgba(var(--glass-white-rgb), 0.88) 0%,
      rgba(var(--glass-pale-rgb), 0.66) 55%,
      rgba(var(--glass-pale-rgb), 0.54) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.78) 0%,
      rgba(var(--glass-pale-rgb), 0.6) 100%
    );
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  box-shadow:
    0 18px 44px -12px rgba(var(--color-brand-500-rgb), 0.3),
    0 6px 16px rgba(var(--color-brand-400-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -4px 12px rgba(var(--color-brand-200-rgb), 0.22),
    inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  transition:
    box-shadow 260ms var(--ease-snappy),
    border-color 260ms var(--ease-snappy);
  isolation: isolate;
  /* overflow stays visible so the Generate button's splash-crown can
     emerge above/around the pill on hover */

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 28px;
    right: 120px;
    height: 16px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.82) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
    z-index: 0;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom aqua refraction */
  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 30%;
    right: 30%;
    height: 14px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.32) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: 0;
  }

  &:focus-within {
    border-color: rgba(var(--color-brand-400-rgb), 0.45);
    box-shadow:
      0 22px 50px -12px rgba(var(--color-brand-500-rgb), 0.42),
      0 8px 20px rgba(var(--color-brand-400-rgb), 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 1),
      inset 0 -4px 14px rgba(var(--color-brand-200-rgb), 0.32),
      inset 0 0 0 1px rgba(255, 255, 255, 0.6);
  }

  @media (max-width: 768px) {
    min-height: 54px;
    padding: 5px 5px 5px 16px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Leading icon (morphs search ↔ X with a drawing animation) ──────────────

export const LeadingIconWrap = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  padding: 0;
  margin: 0;
  background: transparent;
  border: 0;
  cursor: ${({ $active }) => ($active ? "pointer" : "default")};
  outline: none;
  -webkit-tap-highlight-color: transparent;
  color: var(--color-brand-600);
  transition: transform 220ms var(--ease-snappy);
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};

  &:hover {
    transform: ${({ $active }) =>
      $active ? "scale(1.08) rotate(90deg)" : "none"};
  }
  &:active {
    transform: ${({ $active }) =>
      $active ? "scale(0.92) rotate(90deg)" : "none"};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(var(--color-brand-400-rgb), 0.35);
    border-radius: 999px;
  }

  svg {
    width: 22px;
    height: 22px;
    overflow: visible;
    filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.28));
  }

  svg path {
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 1;
    transition: stroke-dashoffset 420ms cubic-bezier(0.7, 0, 0.3, 1);
  }

  .icon-search {
    stroke-dashoffset: ${({ $active }) => ($active ? 1 : 0)};
    transition-delay: ${({ $active }) => ($active ? "0ms" : "260ms")};
  }

  .icon-x-1 {
    stroke-dashoffset: ${({ $active }) => ($active ? 0 : 1)};
    transition-delay: ${({ $active }) => ($active ? "260ms" : "0ms")};
  }

  .icon-x-2 {
    stroke-dashoffset: ${({ $active }) => ($active ? 0 : 1)};
    transition-delay: ${({ $active }) => ($active ? "380ms" : "80ms")};
  }

  @media (prefers-reduced-motion: reduce) {
    svg path {
      transition: none;
    }
    &:hover,
    &:active {
      transform: none;
    }
  }

  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

// ─── Text input ─────────────────────────────────────────────────────────────

export const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 48px;
  padding: 0 8px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: normal;
  position: relative;
  z-index: 1;

  &::placeholder {
    color: var(--color-text-dim);
    font-weight: 400;
    opacity: 1;
  }

  @media (max-width: 768px) {
    font-size: 15px;
    height: 44px;
  }
`;

// ─── Generate slot (wraps the button; kept for layout stability) ──────────

export const GenerateSlot = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  isolation: isolate;
`;

/* Kept as a no-op export so the component import stays valid if any consumer
   still passes <CrownSplash />. Renders nothing — the hover design now lives
   entirely inside GenerateBtn. */
export const CrownSplash = styled.span`
  display: none;
`;

/* Shine sweep that crosses the Generate button on hover */
const generateShine = keyframes`
  0% {
    transform: translateX(-140%) skewX(-18deg);
    opacity: 0;
  }
  30% {
    opacity: 0.85;
  }
  100% {
    transform: translateX(260%) skewX(-18deg);
    opacity: 0;
  }
`;

// ─── Generate button ────────────────────────────────────────────────────────

export const GenerateBtn = styled.button`
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 24px 0 26px;
  height: 48px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.8);
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 55%, var(--color-brand-700) 100%);
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.005em;
  cursor: pointer;
  outline: none;
  z-index: 2;
  overflow: hidden;
  box-shadow:
    0 18px 36px rgba(var(--color-brand-600-rgb), 0.3),
    0 4px 10px rgba(var(--color-brand-600-rgb), 0.14),
    inset 0 2px 0 rgba(255, 255, 255, 0.6),
    inset 0 -4px 8px rgba(var(--color-brand-700-rgb), 0.3);
  animation: ${softPulse} 6s var(--ease-snappy) infinite;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy);

  /* Inner top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 14px;
    right: 14px;
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.65) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Hover shine — a bright white diagonal sweep that moves across the button.
     mix-blend-mode so the sweep LIGHTENS through text rather than covering it. */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 40%;
    pointer-events: none;
    background: linear-gradient(
      100deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(var(--glass-white-rgb), 0.5) 45%,
      rgba(var(--glass-white-rgb), 0.8) 50%,
      rgba(255, 255, 255, 0.5) 55%,
      rgba(255, 255, 255, 0) 100%
    );
    mix-blend-mode: overlay;
    transform: translateX(-140%) skewX(-18deg);
    opacity: 0;
  }

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow:
      0 24px 44px rgba(var(--color-brand-600-rgb), 0.42),
      0 6px 14px rgba(var(--color-brand-600-rgb), 0.22),
      inset 0 2px 0 rgba(255, 255, 255, 0.8),
      inset 0 -4px 10px rgba(var(--color-brand-700-rgb), 0.38),
      inset 0 0 0 1px rgba(255, 255, 255, 0.5);
    filter: brightness(1.08) saturate(1.1);
    animation-play-state: paused;
  }

  &:hover::after {
    animation: ${generateShine} 900ms ease-out;
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
    animation: none;
  }

  svg {
    width: 20px;
    height: 20px;
  }
  @media (max-width: 768px) {
    height: 44px;
    padding: 0 18px 0 20px;
    font-size: 14px;
    gap: 6px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* Inline water-drop icon rendered inside the Generate button.
   Body stays near-white so it pops against the blue button, and all the
   "shine" details (reflection dot, specular, gloss stroke) are light blue
   so they remain visible instead of blending into the white body. */
export const GenerateDrop = styled.span`
  position: relative;
  display: inline-block;
  width: 18px;
  height: 20px;
  flex-shrink: 0;
  margin-bottom: -2px;
  margin-left: -6px;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      /* (1) Bright light-blue reflection dot */
      radial-gradient(
          circle at 30% 38%,
          rgba(130, 205, 255, 1) 0%,
          rgba(var(--color-brand-300-rgb), 0.92) 25%,
          rgba(var(--color-brand-200-rgb), 0) 60%
        )
        0 0 / 18% 14% no-repeat,
      /* (2) Curved gloss stroke — medium-sky blue */
      url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='rgb(108, 196, 255)' opacity='0.92' d='M272 412a12 12 0 0 1-11.34-16a11.89 11.89 0 0 1 11.41-8A60.06 60.06 0 0 0 332 328.07a11.89 11.89 0 0 1 8-11.41A12 12 0 0 1 356 328a84.09 84.09 0 0 1-84 84'/%3E%3C/svg%3E")
        no-repeat center / contain,
      /* (3) Soft elongated specular — light sky blue */
      radial-gradient(
          ellipse 44% 32% at 34% 46%,
          rgba(var(--color-brand-300-rgb), 0.65) 0%,
          rgba(var(--color-brand-200-rgb), 0.3) 50%,
          rgba(var(--color-brand-200-rgb), 0) 100%
        ),
      /* (4) Base body — near-white with a soft cyan center */
      radial-gradient(
          ellipse 115% 100% at 34% 28%,
          #ffffff 0%,
          #f4fbff 30%,
          #e4f4ff 60%,
          var(--color-brand-200) 92%,
          var(--color-brand-300) 100%
        );
    -webkit-mask: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M265.12 60.12a12 12 0 0 0-18.23 0C215.23 97.15 112 225.17 112 320c0 88.37 55.64 144 144 144s144-55.63 144-144c0-94.83-103.23-222.85-134.88-259.88'/%3E%3C/svg%3E")
      no-repeat center / contain;
    mask: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M265.12 60.12a12 12 0 0 0-18.23 0C215.23 97.15 112 225.17 112 320c0 88.37 55.64 144 144 144s144-55.63 144-144c0-94.83-103.23-222.85-134.88-259.88'/%3E%3C/svg%3E")
      no-repeat center / contain;
    filter: drop-shadow(0 1px 2px rgba(var(--color-brand-900-rgb), 0.45))
      drop-shadow(0 0 4px rgba(var(--glass-white-rgb), 0.35));
  }

  @media (max-width: 768px) {
    width: 16px;
    height: 18px;
  }
`;
